import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getOktaAuth } from "./auth.ts";
import { getConfig } from "./runtimeConfig.ts";

let apiInstancePromise: Promise<AxiosInstance> | null = null;

/**
 * Initializes the Axios API instance with runtime configuration
 * Returns a promise that resolves to the configured axios instance
 */
async function initializeApiInstance() {
  if (!apiInstancePromise) {
    apiInstancePromise = (async () => {
      const config = getConfig();

      if (!config.VITE_API_HOST_VALUE) {
        throw new Error(
          "VITE_API_HOST_VALUE is not configured. " +
          "Please ensure config.json is properly deployed or .env.development is configured."
        );
      }

      const instance = axios.create({
        baseURL: config.VITE_API_HOST_VALUE,
      });

      instance.interceptors.request.use(requestHandler);

      return instance;
    })();
  }
  return apiInstancePromise;
}

const ApiInstance = new Proxy({} as AxiosInstance, {
  get(_target, prop) {
    // Return a function that initializes the instance and then calls the method
    return (...args: any[]) => {
      return initializeApiInstance().then(instance => {
        const method = (instance as any)[prop];
        if (typeof method === 'function') {
          return method.apply(instance, args);
        }
        return method;
      });
    };
  }
})

export const requestHandler = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const oktaAuth = getOktaAuth();
  const accessToken = await oktaAuth.getOrRenewAccessToken()

  if (!accessToken) {
    oktaAuth.tokenManager.clear()
    await oktaAuth.signInWithRedirect()
  } else {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return config
}

export { ApiInstance as api }
